import { ApiError } from './apiClient';

type StructuredPolicyError = {
  detail?: string;
  code?: string;
  plan?: string;
  limit?: number | null;
  used?: number | null;
  remaining?: number | null;
  period_end?: string | null;
  upgrade_hint?: string | null;
  signal_state?: string | null;
  has_historical_signal?: boolean | null;
  has_runtime_signal?: boolean | null;
  precondition?: string | null;
};

const getStructuredPolicyError = (error: unknown): StructuredPolicyError | null => {
  if (error instanceof ApiError && error.body) {
    const body = error.body as any;
    if (body && typeof body.detail === 'object' && body.detail !== null) {
      return body.detail as StructuredPolicyError;
    }
    if (body && typeof body === 'object' && body.code) {
      return body as StructuredPolicyError;
    }
  }

  const maybeError = error as any;
  if (maybeError?.detail && typeof maybeError.detail === 'object') {
    return maybeError.detail as StructuredPolicyError;
  }

  return null;
};

export const formatApiError = (
  error: unknown,
  fallback = 'Something went wrong.'
): string => {
  const structured = getStructuredPolicyError(error);

  if (structured?.code === 'project_limit_exceeded') {
    if (structured.limit === 1) {
      return 'Your current plan allows 1 project. Upgrade to Team for up to 5 projects.';
    }

    return [
      structured.detail || 'Your current plan project limit has been reached.',
      structured.upgrade_hint,
    ]
      .filter(Boolean)
      .join(' ');
  }

  if (structured?.code === 'usage_limit_exceeded') {
    return 'You have reached your monthly selection run limit. Upgrade to Team or add capacity to continue.';
  }

  if (structured?.code === 'feature_not_available') {
    return [
      structured.detail || 'This feature is not available on your current plan.',
      structured.upgrade_hint,
    ]
      .filter(Boolean)
      .join(' ');
  }

  if (structured?.code === 'history_window_exceeded') {
    return [
      structured.detail || 'This history entry is outside your plan history window.',
      structured.upgrade_hint,
    ]
      .filter(Boolean)
      .join(' ');
  }

  if (structured?.code === 'insufficient_signal') {
    return 'Selection needs a signal source before it can run.';
  }

  if (structured?.code === 'demo_history_precondition_failed') {
    return [
      structured.detail || 'Sample execution history could not be generated.',
      structured.precondition,
    ]
      .filter(Boolean)
      .join(' ');
  }

  if (error instanceof ApiError) {
    return error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message || fallback;
  }

  if (typeof error === 'string') {
    return error;
  }

  return fallback;
};
